'use strict';

import { encodeOggVorbis } from './vorbis-ogg-encoder.js';

// ── FFT (Cooley-Tukey, radix-2 DIT) ─────────────────────────────────────────

function bitReverse(n) {
  const a = new Uint32Array(n);
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    a[i] = j;
  }
  return a;
}

function hannWindow(n) {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (n - 1)));
  return w;
}

// ── Noise helpers (module-level, no state) ───────────────────────────────────

// Fast 32-bit integer hash of (seed, ix, iy) → float [0, 1)
function noiseHash(seed, ix, iy) {
  let h = (seed + Math.imul(ix, 374761393) + Math.imul(iy, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = h ^ (h >>> 16);
  return (h >>> 0) / 0x100000000;
}

// Smooth 2D value noise via bilinear interpolation with Hermite smoothing.
// Returns [0, 1]. Scale is applied externally via x/y before calling.
function smoothNoise2D(seed, x, y) {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = x - ix, fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const v00 = noiseHash(seed, ix,     iy);
  const v10 = noiseHash(seed, ix + 1, iy);
  const v01 = noiseHash(seed, ix,     iy + 1);
  const v11 = noiseHash(seed, ix + 1, iy + 1);
  return v00*(1-ux)*(1-uy) + v10*ux*(1-uy) + v01*(1-ux)*uy + v11*ux*uy;
}

// Shortest distance from point (px,py) to segment (ax,ay)→(bx,by)
function distToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx*dx + dy*dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px-ax)*dx + (py-ay)*dy) / lenSq));
  return Math.hypot(px - (ax + t*dx), py - (ay + t*dy));
}

// ── Save / load helpers ───────────────────────────────────────────────────────

// Truncate a string to at most maxBytes when UTF-8-encoded, without splitting
// a multi-byte codepoint.
function _utf8Trunc(str, maxBytes) {
  const b = new TextEncoder().encode(str);
  if (b.byteLength <= maxBytes) return b;
  let i = maxBytes;
  while (i > 0 && (b[i] & 0xC0) === 0x80) i--;
  return b.subarray(0, i);
}

function _deflate(buf) {
  const cs = new CompressionStream('deflate');
  const w  = cs.writable.getWriter();
  w.write(buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf);
  w.close();
  return new Response(cs.readable).arrayBuffer();
}

function _inflate(buf) {
  const ds = new DecompressionStream('deflate');
  const w  = ds.writable.getWriter();
  w.write(buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf);
  w.close();
  return new Response(ds.readable).arrayBuffer();
}

// Encode a Uint8Array to a base64 string without stack-overflowing on large inputs.
function _toBase64(u8) {
  let str = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < u8.length; i += CHUNK) {
    str += String.fromCharCode(...u8.subarray(i, Math.min(i + CHUNK, u8.length)));
  }
  return btoa(str);
}

function _fromBase64(b64) {
  const str = atob(b64);
  const u8  = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) u8[i] = str.charCodeAt(i);
  return u8;
}

// ── Warp / triangle helpers ───────────────────────────────────────────────────

// Compute the 2D affine [a,b,c,d,e,f] that maps the source triangle to the
// destination triangle (for use with ctx.setTransform before drawImage).
function _affineFromTriangle(sx0,sy0, sx1,sy1, sx2,sy2,
                              dx0,dy0, dx1,dy1, dx2,dy2) {
  const det = sx0*(sy1-sy2) + sx1*(sy2-sy0) + sx2*(sy0-sy1);
  if (Math.abs(det) < 1e-10) return null;
  return [
    (dx0*(sy1-sy2) + dx1*(sy2-sy0) + dx2*(sy0-sy1)) / det,
    (dy0*(sy1-sy2) + dy1*(sy2-sy0) + dy2*(sy0-sy1)) / det,
    (dx0*(sx2-sx1) + dx1*(sx0-sx2) + dx2*(sx1-sx0)) / det,
    (dy0*(sx2-sx1) + dy1*(sx0-sx2) + dy2*(sx1-sx0)) / det,
    (dx0*(sx1*sy2-sx2*sy1) + dx1*(sx2*sy0-sx0*sy2) + dx2*(sx0*sy1-sx1*sy0)) / det,
    (dy0*(sx1*sy2-sx2*sy1) + dy1*(sx2*sy0-sx0*sy2) + dy2*(sx0*sy1-sx1*sy0)) / det,
  ];
}

// Clip to the destination triangle, apply the affine mapping, then drawImage.
function _drawWarpTriangle(ctx, img,
                            sx0,sy0, sx1,sy1, sx2,sy2,
                            dx0,dy0, dx1,dy1, dx2,dy2) {
  const m = _affineFromTriangle(sx0,sy0, sx1,sy1, sx2,sy2,
                                 dx0,dy0, dx1,dy1, dx2,dy2);
  if (!m) return;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(dx0, dy0); ctx.lineTo(dx1, dy1); ctx.lineTo(dx2, dy2);
  ctx.closePath();
  ctx.clip();
  ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
}

// Returns true if (px,py) is inside the triangle (ax,ay)-(bx,by)-(cx,cy).
function _pointInTriangle(px,py, ax,ay, bx,by, cx,cy) {
  const d1 = (px-bx)*(ay-by) - (ax-bx)*(py-by);
  const d2 = (px-cx)*(by-cy) - (bx-cx)*(py-cy);
  const d3 = (px-ax)*(cy-ay) - (cx-ax)*(py-ay);
  return !((d1<0||d2<0||d3<0) && (d1>0||d2>0||d3>0));
}

// ── Display compression ───────────────────────────────────────────────────────

// Undo the log+gamma display compression before using pixel brightness as a
// synthesis amplitude. This restores STFT leakage bins to their true (small)
// linear magnitudes so they don't beat loudly against the dominant frequency.
const DISP_LOG100 = Math.log1p(100);
const DISP_GAMMA  = 1 / 0.85;

/**
 * Spectral Painter: draw a spectrogram on a canvas (X = time, Y = log frequency,
 * brightness = amplitude), then synthesize it to audio via Griffin-Lim, or go the
 * other way and load audio/PNG into the canvas.
 *
 * Usage: const painter = new SpectralPainter(); painter.setup(canvasEl);
 * All UI feedback (slider readouts, status text, progress, playhead position,
 * busy/play state) is surfaced as CustomEvents on the instance so a view layer
 * (plain DOM here, Vue components eventually) can bind to it without the class
 * knowing anything about the page it lives on.
 */
export class SpectralPainter extends EventTarget {
  constructor() {
    super();

    this.fileExtension = 'spnt';
    this.sessionName   = '';
    this.storagePrefix = 'spnt:';

    this.sampleRate   = 44100;
    this.duration     = 2.0;
    this.totalSamples = Math.round(this.sampleRate * this.duration);
    this.fftSize      = 4096;

    this.hann  = hannWindow(this.fftSize);
    this.brFft = bitReverse(this.fftSize);

    // Frequency ↔ pixel-row mapping (logarithmic scale). y=0 (top) = high freq.
    this.freqMinBin = 1;                     // ~10.8 Hz with fftSize=4096
    this.freqMaxBin = this.fftSize / 2 - 1;  // 2047 bins ≈ 22 kHz
    this.glBins     = this.fftSize / 2 + 1;  // DC → Nyquist inclusive

    // Tool state
    this.brushSize     = 10;
    this.brushSoftness = 2;
    this.brushOpacity  = 1;
    this.eraseMode     = false;
    this.brushType     = 'paint';  // 'paint' | 'noise'
    this.noiseScale    = 10;
    this._noiseSeed    = 0;        // randomised each mousedown
    this.glItersPlay   = 15;
    this.glItersExport = 200;

    // Layer state — each layer is { id, name, visible, canvas, ctx }
    // layers[0] is the bottom of the stack, layers[last] renders on top.
    this.layers          = [];
    this.selectedLayerId = null;
    this._layerCounter   = 0;

    // Canvas, set by setup()
    this.canvas = null;
    this.ctx    = null;
    this.cw     = 0;
    this.ch     = 0;

    // Tables that depend on canvas size, computed by setup()
    this.wolaNorm   = null;
    this.wolaPeak   = 0;
    this.binRowLo   = null;
    this.binRowHi   = null;
    this.binRowFrac = null;

    this._isDrawing = false;
    this._lastPt    = null;

    this.audioCtx   = null;
    this.playSource = null;
    this.playStart  = null;
    this._animFrame = null;
    this._playCache = null;  // synthesized samples, reused until canvas or iters changes

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp   = this._onPointerUp.bind(this);
    this._onTouchStart  = this._onTouchStart.bind(this);
    this._onTouchMove   = this._onTouchMove.bind(this);
    this._selMoveHandler = (e) => this._selOnPointerMove(e);
    this._selUpHandler   = (e) => this._selOnPointerUp(e);
    this._onKeyDown     = this._onKeyDown.bind(this);
    this._onKeyUp       = this._onKeyUp.bind(this);
    this._onWindowBlur  = this._onWindowBlur.bind(this);

    // Alt temporarily overrides erase mode while held
    this._altErasing  = false;
    this._preAltErase = false;

    // Shift: axis-locked drawing
    this._shiftDown  = false;
    this._axisLock   = null;   // 'x' | 'y' | null
    this._axisOrigin = null;   // mousedown point used as the lock origin

    // Selection / transform tool
    this._tool = 'brush';
    this._sel  = {
      phase: 'idle', handle: null,
      marqueeStart: null, marqueeRect: null, altMarqueeRect: null,
      floatCanvas: null, floatCtx: null,
      cx: 0, cy: 0, hw: 0, hh: 0, origHw: 0, origHh: 0, θ: 0, flipX: false, flipY: false,
      dragAnchorWorld: null, dragStart: null, hasMoved: false,
      warpCorners: null,
    };
    this._overlayCanvas = null;
    this._overlayCtx    = null;
    this._marchOffset   = 0;
    this._marchRaf      = null;

    // Undo / redo
    this._undoStack      = [];
    this._redoStack      = [];
    this._undoLimit      = 100;
    this._actionSnapshot = null; // captured at action start, pushed on commit
    this._setupDone      = false; // gates undo pushes during initial setup
  }

  // ── Setup ────────────────────────────────────────────────────────────────

  setup(canvas) {
    this.canvas = canvas;
    this.ctx    = canvas.getContext('2d');
    this.cw     = canvas.width;
    this.ch     = canvas.height;

    this._computeWola();
    this._computeBinRowMapping();

    canvas.addEventListener('mousedown',  this._onPointerDown);
    canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    canvas.addEventListener('touchmove',  this._onTouchMove,  { passive: false });
    canvas.addEventListener('touchend',   this._onPointerUp);

    document.addEventListener('keydown',  this._onKeyDown);
    document.addEventListener('keyup',    this._onKeyUp);
    window.addEventListener('blur',       this._onWindowBlur);

    // Start with one blank layer. addLayer() calls _renderComposite() which
    // paints the black background, and fires the initial layerschange event.
    this.addLayer('Layer 1');
    this._setupDone = true;
  }

  // WOLA normalisation: sum of squared Hann windows at each sample position,
  // precomputed so ISTFT can divide it out in one pass.
  _computeWola() {
    const norm = new Float32Array(this.totalSamples);
    const hop  = this.totalSamples / this.cw;
    for (let x = 0; x < this.cw; x++) {
      const s = Math.round(x * hop);
      for (let n = 0; n < this.fftSize; n++) {
        const p = s + n;
        if (p < this.totalSamples) norm[p] += this.hann[n] * this.hann[n];
      }
    }
    this.wolaNorm = norm;

    // Peak (interior) WOLA value — used to clamp the divisor so that edge samples
    // where only a few frames overlap are not artificially amplified into huge
    // spikes that would swamp the peak-normalisation at the end of synthesize().
    let m = 0;
    for (let p = 0; p < this.totalSamples; p++) if (norm[p] > m) m = norm[p];
    this.wolaPeak = m;
  }

  // Per-STFT-bin canvas row mapping with linear interpolation. For each linear-
  // frequency bin k, compute the exact fractional canvas row position on the
  // log-frequency axis and split it into floor/ceil + fraction, so magnitude for
  // bins between two canvas rows blends smoothly instead of jumping at row edges.
  _computeBinRowMapping() {
    this.binRowLo   = new Uint16Array(this.glBins);
    this.binRowHi   = new Uint16Array(this.glBins);
    this.binRowFrac = new Float32Array(this.glBins);
    const logRange = Math.log(this.freqMaxBin / this.freqMinBin);
    for (let k = 1; k <= this.freqMaxBin; k++) {
      const yc = (this.ch - 1) * (1 - Math.log(k / this.freqMinBin) / logRange);
      const lo = Math.max(0, Math.min(this.ch - 1, Math.floor(yc)));
      const hi = Math.max(0, Math.min(this.ch - 1, Math.ceil(yc)));
      this.binRowLo[k]   = lo;
      this.binRowHi[k]   = hi;
      this.binRowFrac[k] = yc - Math.floor(yc);
    }
    // k=0 (DC) and k=glBins-1 (Nyquist) stay at 0 — rows don't matter
  }

  // ── Layer management (internal) ──────────────────────────────────────────

  _makeLayer(name) {
    const id     = `L${++this._layerCounter}`;
    const canvas = document.createElement('canvas');
    canvas.width  = this.cw;
    canvas.height = this.ch;
    const ctx = canvas.getContext('2d');
    return { id, name, visible: true, canvas, ctx };
  }

  _selectedLayer() {
    return this.layers.find(l => l.id === this.selectedLayerId)
        ?? this.layers[this.layers.length - 1];
  }

  // Redraws the main canvas: black background, then visible layers in stack order.
  // The composite is what synthesis reads, so the main canvas always reflects
  // the current layer state — a hidden layer is therefore silent as well as
  // invisible. Also clears the play cache since the content changed.
  _renderComposite() {
    this._playCache = null;
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, this.cw, this.ch);
    for (const layer of this.layers) {
      if (layer.visible) this.ctx.drawImage(layer.canvas, 0, 0);
    }
    // Floating selection content is part of the visual (and synthesis) composite
    const sel = this._sel;
    if (sel.floatCanvas && sel.phase === 'selected') {
      if (sel.warpCorners) {
        this._drawWarpedFloat(this.ctx);
      } else {
        this.ctx.save();
        this.ctx.translate(sel.cx, sel.cy);
        this.ctx.rotate(sel.θ);
        if (sel.flipX || sel.flipY) this.ctx.scale(sel.flipX ? -1 : 1, sel.flipY ? -1 : 1);
        this.ctx.drawImage(sel.floatCanvas, -sel.hw, -sel.hh, sel.hw * 2, sel.hh * 2);
        this.ctx.restore();
      }
    }
  }

  // Emits layerschange with canvas references so the view can generate thumbnails.
  _emitLayersChange() {
    this._emit('layerschange', {
      // Emitted in stack order (index 0 = bottom). Panel reverses for display.
      layers:     this.layers.map(({ id, name, visible, canvas }) => ({ id, name, visible, canvas })),
      selectedId: this.selectedLayerId,
    });
  }

  // ── Undo / redo ───────────────────────────────────────────────────────────

  _captureSelSnapshot() {
    const sel = this._sel;
    if (sel.phase === 'idle') return null;
    return {
      phase: sel.phase,
      cx: sel.cx, cy: sel.cy,
      hw: sel.hw, hh: sel.hh,
      origHw: sel.origHw, origHh: sel.origHh,
      θ: sel.θ, flipX: sel.flipX, flipY: sel.flipY,
      warpCorners: sel.warpCorners
        ? { tl: {...sel.warpCorners.tl}, tr: {...sel.warpCorners.tr},
            bl: {...sel.warpCorners.bl}, br: {...sel.warpCorners.br} }
        : null,
      floatW: sel.floatCanvas ? sel.floatCanvas.width  : 0,
      floatH: sel.floatCanvas ? sel.floatCanvas.height : 0,
      floatData: sel.floatCanvas
        ? sel.floatCtx.getImageData(0, 0, sel.floatCanvas.width, sel.floatCanvas.height)
        : null,
    };
  }

  _captureSnapshot() {
    return {
      layers: this.layers.map(l => ({
        id: l.id, name: l.name, visible: l.visible,
        imageData: l.ctx.getImageData(0, 0, this.cw, this.ch),
      })),
      selectedLayerId: this.selectedLayerId,
      sel: this._captureSelSnapshot(),
    };
  }

  _restoreSnapshot(snap) {
    this._selStopMarch();
    this._marchRaf = null;

    // Rebuild layer objects from stored pixel data
    this.layers = snap.layers.map(ls => {
      const canvas = document.createElement('canvas');
      canvas.width = this.cw; canvas.height = this.ch;
      const ctx = canvas.getContext('2d');
      ctx.putImageData(ls.imageData, 0, 0);
      return { id: ls.id, name: ls.name, visible: ls.visible, canvas, ctx };
    });
    this.selectedLayerId = snap.selectedLayerId;

    // Restore selection state
    const ss = snap.sel;
    if (!ss) {
      this._sel = {
        phase: 'idle', handle: null,
        marqueeStart: null, marqueeRect: null, altMarqueeRect: null,
        floatCanvas: null, floatCtx: null,
        cx: 0, cy: 0, hw: 0, hh: 0, origHw: 0, origHh: 0,
        θ: 0, flipX: false, flipY: false,
        dragAnchorWorld: null, dragStart: null, hasMoved: false,
        warpCorners: null,
      };
      if (this._overlayCtx) {
        this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
      }
    } else {
      let floatCanvas = null, floatCtx = null;
      if (ss.floatData) {
        floatCanvas = document.createElement('canvas');
        floatCanvas.width = ss.floatW; floatCanvas.height = ss.floatH;
        floatCtx = floatCanvas.getContext('2d');
        floatCtx.putImageData(ss.floatData, 0, 0);
      }
      this._sel = {
        phase: ss.phase, handle: null,
        marqueeStart: null, marqueeRect: null, altMarqueeRect: null,
        floatCanvas, floatCtx,
        cx: ss.cx, cy: ss.cy,
        hw: ss.hw, hh: ss.hh,
        origHw: ss.origHw, origHh: ss.origHh,
        θ: ss.θ, flipX: ss.flipX, flipY: ss.flipY,
        dragAnchorWorld: null, dragStart: null, hasMoved: false,
        warpCorners: ss.warpCorners
          ? { tl: {...ss.warpCorners.tl}, tr: {...ss.warpCorners.tr},
              bl: {...ss.warpCorners.bl}, br: {...ss.warpCorners.br} }
          : null,
      };
      if (ss.phase === 'selected') this._selStartMarch();
      this._selRender();
    }

    this._renderComposite();
    this._emitLayersChange();
  }

  // Capture the current state as the pre-action baseline for this action.
  _beginAction() {
    this._actionSnapshot = this._captureSnapshot();
  }

  // Push the baseline snapshot to the undo stack, clearing redo.
  _commitAction() {
    if (!this._actionSnapshot || !this._setupDone) return;
    this._undoStack.push(this._actionSnapshot);
    while (this._undoStack.length > this._undoLimit) this._undoStack.shift();
    this._redoStack = [];
    this._actionSnapshot = null;
    this._emitHistoryChange();
  }

  _cancelAction() { this._actionSnapshot = null; }

  // Convenience: snapshot + immediately push (for non-drag actions).
  _pushUndo() {
    if (!this._setupDone) return;
    this._beginAction();
    this._commitAction();
  }

  _emitHistoryChange() {
    this._emit('historychange', {
      canUndo: this._undoStack.length > 0,
      canRedo: this._redoStack.length > 0,
    });
  }

  undo() {
    if (this._undoStack.length === 0) return;
    this._redoStack.push(this._captureSnapshot());
    this._restoreSnapshot(this._undoStack.pop());
    this._emitHistoryChange();
  }

  redo() {
    if (this._redoStack.length === 0) return;
    this._undoStack.push(this._captureSnapshot());
    this._restoreSnapshot(this._redoStack.pop());
    this._emitHistoryChange();
  }

  setUndoLimit(n) {
    this._undoLimit = Math.max(1, Math.min(200, parseInt(n, 10)));
    while (this._undoStack.length > this._undoLimit) this._undoStack.shift();
    this._emit('undolimitchange', { value: this._undoLimit });
    this._emitHistoryChange();
  }

  // ── Layer management (public API) ─────────────────────────────────────────

  // A floating selection belongs to the layer it was cut from, so it must be
  // baked down before selectedLayerId moves — otherwise it lands on whichever
  // layer happens to be selected when it is eventually committed.
  _commitSelectionToOwnLayer() {
    if (this._sel.phase === 'idle') return;
    this._beginAction();
    this._selCommit();
    this._selRender();
    this._commitAction();
  }

  addLayer(name) {
    this._commitSelectionToOwnLayer();
    this._pushUndo();
    const label = name ?? `Layer ${this.layers.length + 1}`;
    const layer = this._makeLayer(label);
    this.layers.push(layer);
    this.selectedLayerId = layer.id;
    this._renderComposite();
    this._emitLayersChange();
  }

  removeLayer(id) {
    const idx = this.layers.findIndex(l => l.id === id);
    if (idx === -1 || this.layers.length <= 1) return;
    this._pushUndo();
    // The float was cut from this layer, so it is discarded along with it
    // rather than baked onto the layer that takes over the selection.
    if (this.selectedLayerId === id && this._sel.phase !== 'idle') {
      this._selClearState();
      this._selRender();
    }
    this.layers.splice(idx, 1);
    if (this.selectedLayerId === id) {
      this.selectedLayerId = this.layers[Math.min(idx, this.layers.length - 1)].id;
    }
    this._renderComposite();
    this._emitLayersChange();
  }

  selectLayer(id) {
    if (!this.layers.find(l => l.id === id)) return;
    this._beginAction();
    if (this._sel.phase !== 'idle') {
      this._selCommit();
      this._selRender();
    }
    this.selectedLayerId = id;
    this._commitAction();
    this._emitLayersChange();
  }

  renameLayer(id, name) {
    const layer = this.layers.find(l => l.id === id);
    if (!layer || !name) return;
    layer.name = name;
    this._emitLayersChange();
  }

  // A float is cut from its layer and lives outside it, so it would keep
  // rendering (and sounding) after the layer it came from was hidden. Bake it
  // down first so hiding a layer hides everything that belongs to it.
  toggleLayerVisibility(id) {
    const layer = this.layers.find(l => l.id === id);
    if (!layer) return;
    this._commitSelectionToOwnLayer();
    this._pushUndo();
    layer.visible = !layer.visible;
    this._renderComposite();
    this._emitLayersChange();
  }

  // Moves a layer to a new stack index (0 = bottom, length-1 = top).
  // Layers between old and new positions shift to fill the gap.
  moveLayer(id, newIndex) {
    const idx = this.layers.findIndex(l => l.id === id);
    if (idx === -1) return;
    newIndex = Math.max(0, Math.min(this.layers.length - 1, newIndex));
    if (idx === newIndex) return;
    this._pushUndo();
    const [layer] = this.layers.splice(idx, 1);
    this.layers.splice(newIndex, 0, layer);
    this._renderComposite();
    this._emitLayersChange();
  }

  _yToBin(y) {
    const t = (this.ch - 1 - y) / (this.ch - 1); // 0 at bottom, 1 at top
    return Math.round(this.freqMinBin * Math.pow(this.freqMaxBin / this.freqMinBin, t));
  }

  _binToY(k) {
    const kc = Math.max(this.freqMinBin, Math.min(this.freqMaxBin, k));
    const t  = Math.log(kc / this.freqMinBin) / Math.log(this.freqMaxBin / this.freqMinBin);
    return Math.round(this.ch - 1 - t * (this.ch - 1));
  }

  // Axis tick data for a view layer to render (this class draws no axis chrome).
  getTimeAxisTicks(step = 0.5) {
    const ticks = [];
    for (let t = 0; t <= this.duration; t += step) {
      ticks.push({ time: t, x: Math.round(t / this.duration * this.cw) });
    }
    return ticks;
  }

  getFrequencyAxisTicks() {
    const freqs = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
    const ticks = [];
    for (const f of freqs) {
      const k = Math.round(f / (this.sampleRate / this.fftSize));
      if (k < this.freqMinBin || k > this.freqMaxBin) continue;
      ticks.push({ freq: f, y: this._binToY(k) });
    }
    return ticks;
  }

  // ── Tool state (called by control event listeners on the outside) ─────────

  setBrushSize(value) {
    this.brushSize = parseInt(value, 10);
    this._emit('brushsizechange', { value: this.brushSize });
  }

  setBrushSoftness(value) {
    this.brushSoftness = parseInt(value, 10);
    this._emit('softnesschange', { value: this.brushSoftness });
  }

  setBrushOpacity(value) {
    this.brushOpacity = Math.max(0, Math.min(1, value));
    this._emit('brushopacitychange', { value: this.brushOpacity });
  }

  setGlItersPlay(value) {
    this.glItersPlay = parseInt(value, 10);
    this._playCache  = null;
    this._emit('glitersplaychange', { value: this.glItersPlay });
  }

  setGlItersExport(value) {
    this.glItersExport = parseInt(value, 10);
    this._emit('glitersexportchange', { value: this.glItersExport });
  }

  setName(name) {
    this.sessionName = typeof name === 'string' ? name : '';
    this._emit('namechange', { name: this.sessionName });
  }

  // Returns a filesystem-safe filename base (no extension). Falls back to
  // 'untitled' if the session name is blank or consists entirely of
  // stripped characters.
  _safeFilename() {
    const safe = this.sessionName
      .trim()
      .replace(/[/\\:*?"<>|]/g, '')
      .trim()
      .slice(0, 100);
    return safe || 'untitled';
  }

  toggleErase() {
    this.eraseMode = !this.eraseMode;
    this._emit('erasechange', { active: this.eraseMode });
  }

  toggleNoiseBrush() {
    this.brushType = this.brushType === 'noise' ? 'paint' : 'noise';
    this._emit('brushtypechange', { type: this.brushType });
  }

  setNoiseScale(value) {
    this.noiseScale = Math.max(1, parseInt(value, 10));
    this._emit('noisescalechange', { value: this.noiseScale });
  }

  clear() {
    const layer = this._selectedLayer();
    if (!layer) return;
    this._pushUndo();
    layer.ctx.clearRect(0, 0, this.cw, this.ch);
    this._renderComposite();
    this._emitLayersChange();
  }

  setTool(tool) {
    if (this._tool === tool) return;
    if (this._tool === 'select' && this._sel.phase !== 'idle') {
      this._beginAction();
      this._selCommit();
      this._commitAction();
    }
    this._tool = tool;
    if (this._overlayCanvas) {
      const on = tool === 'select';
      this._overlayCanvas.style.pointerEvents = on ? 'auto' : 'none';
      this._overlayCanvas.style.cursor        = on ? 'crosshair' : '';
      if (!on) this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
    }
    this._emit('toolchange', { tool });
  }

  setupOverlay(overlayCanvas) {
    this._overlayCanvas = overlayCanvas;
    this._overlayCtx    = overlayCanvas.getContext('2d');
    overlayCanvas.addEventListener('mousedown', (e) => this._selOnPointerDown(e));
  }

  deselect() {
    if (this._sel.phase === 'idle') return;
    this._beginAction();
    this._selCommit();
    this._commitAction();
  }

  // Select the bounding box of all non-transparent pixels on the current layer.
  selectAll() {
    const layer = this._selectedLayer();
    if (!layer) return;

    this._beginAction();

    // Bake any existing floating selection back into the layer first.
    if (this._sel.phase !== 'idle') {
      this._selCommit();
      this._selRender();
    }

    // Scan for the axis-aligned bounding box of non-transparent pixels.
    const data = layer.ctx.getImageData(0, 0, this.cw, this.ch).data;
    let x0 = this.cw, x1 = -1, y0 = this.ch, y1 = -1;
    for (let y = 0; y < this.ch; y++) {
      for (let x = 0; x < this.cw; x++) {
        if (data[(y * this.cw + x) * 4 + 3] > 0) {
          if (x < x0) x0 = x; if (x > x1) x1 = x;
          if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) { this._cancelAction(); return; } // layer is empty

    // Switch to select tool so the selection is visible and interactive.
    if (this._tool !== 'select') {
      this._tool = 'select';
      if (this._overlayCanvas) {
        this._overlayCanvas.style.pointerEvents = 'auto';
        this._overlayCanvas.style.cursor = 'crosshair';
      }
      this._emit('toolchange', { tool: 'select' });
    }

    this._sel.marqueeRect = { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
    if (!this._selCutToFloat()) { this._cancelAction(); return; }
    this._sel.phase = 'selected';
    this._selStartMarch();
    this._commitAction();
    this._renderComposite();
    this._selRender();
    this._emitLayersChange();
  }

  // ── Keyboard: Alt = temporary erase override ─────────────────────────────

  _onKeyDown(e) {
    if (e.key === 'Escape' && this._tool === 'select' && this._sel.phase !== 'idle') {
      this._beginAction();
      this._selCommit();
      this._commitAction();
      return;
    }
    if (e.key === 'Alt') {
      e.preventDefault();
      if (this._tool !== 'select' && !this._altErasing) {
        this._altErasing  = true;
        this._preAltErase = this.eraseMode;
        this.eraseMode    = true;
        this._emit('erasechange', { active: true });
      }
    }
    if (e.key === 'Shift') {
      this._shiftDown = true;
    }
  }

  _onKeyUp(e) {
    if (e.key === 'Alt' && this._altErasing) {
      this._altErasing = false;
      this.eraseMode   = this._preAltErase;
      this._emit('erasechange', { active: this.eraseMode });
    }
    if (e.key === 'Shift') {
      this._shiftDown = false;
      this._axisLock  = null;
    }
  }

  // If the window loses focus while Alt/Shift is held the keyup never fires,
  // so reset both overrides when focus leaves the page.
  _onWindowBlur() {
    if (this._altErasing) {
      this._altErasing = false;
      this.eraseMode   = this._preAltErase;
      this._emit('erasechange', { active: this.eraseMode });
    }
    this._shiftDown = false;
    this._axisLock  = null;
  }

  // ── Drawing (canvas pointer events, bound internally by setup()) ──────────

  _getPos(e) {
    const r = this.canvas.getBoundingClientRect();
    const scaleX = this.cw / r.width, scaleY = this.ch / r.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - r.left) * scaleX,
               y: (e.touches[0].clientY - r.top)  * scaleY };
    }
    return { x: (e.clientX - r.left) * scaleX,
             y: (e.clientY - r.top)  * scaleY };
  }

  _paintStroke(from, to) {
    const size  = this.brushSize;
    const soft  = this.brushSoftness;
    const erase = this.eraseMode;
    const layer = this._selectedLayer();
    if (!layer) return;
    const ctx = layer.ctx;

    ctx.save();
    ctx.globalAlpha              = this.brushOpacity;
    ctx.globalCompositeOperation = erase ? 'destination-out' : 'source-over';

    if (soft > 0) {
      // Soft brush via radial gradient
      const grad = ctx.createRadialGradient(to.x, to.y, 0, to.x, to.y, size);
      grad.addColorStop(0,   '#ffffffff');
      grad.addColorStop(0.4, '#ffffffcc');
      grad.addColorStop(1,   '#ffffff00');
      ctx.strokeStyle = grad;
      ctx.lineWidth   = size * 2;
    } else {
      ctx.strokeStyle = erase ? 'rgba(0,0,0,1)' : '#fff';
      ctx.lineWidth   = size;
    }
    ctx.lineCap  = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.restore();

    this._renderComposite();
  }

  // Dispatches to the correct brush implementation for the current brushType.
  _applyStroke(from, to) {
    if (this.brushType === 'noise') {
      this._paintNoiseStroke(from, to);
    } else {
      this._paintStroke(from, to);
    }
  }

  // Noise brush: samples a per-stroke smooth noise field and composites it
  // pixel-by-pixel onto the selected layer within the brush footprint.
  // Erase mode removes with the same noise pattern.
  _paintNoiseStroke(from, to) {
    const layer = this._selectedLayer();
    if (!layer) return;
    const { ctx } = layer;
    const CW = this.cw, CH = this.ch;
    const size     = this.brushSize;
    const soft     = this.brushSoftness;
    const erase    = this.eraseMode;
    const seed     = this._noiseSeed;
    const invScale = 1 / this.noiseScale;

    // Tight bounding box around the stroke segment, padded by brush radius
    const x0 = Math.max(0,      Math.floor(Math.min(from.x, to.x) - size));
    const y0 = Math.max(0,      Math.floor(Math.min(from.y, to.y) - size));
    const x1 = Math.min(CW - 1, Math.ceil( Math.max(from.x, to.x) + size));
    const y1 = Math.min(CH - 1, Math.ceil( Math.max(from.y, to.y) + size));
    const w = x1 - x0 + 1, h = y1 - y0 + 1;
    if (w <= 0 || h <= 0) return;

    const imgData = ctx.getImageData(x0, y0, w, h);
    const d = imgData.data;

    for (let py = y0; py <= y1; py++) {
      for (let px = x0; px <= x1; px++) {
        const dist = distToSegment(px, py, from.x, from.y, to.x, to.y);
        if (dist >= size) continue;

        // Brush coverage — matches the shape of the canvas gradient brush
        let coverage;
        if (soft === 0) {
          coverage = 1;
        } else {
          const t = dist / size;
          coverage = t < 0.4 ? 1 : Math.max(0, 1 - (t - 0.4) / 0.6);
        }

        const noiseVal = smoothNoise2D(seed, px * invScale, py * invScale);
        const srcA = coverage * noiseVal * 0.25 * this.brushOpacity;
        if (srcA <= 0) continue;

        const i = ((py - y0) * w + (px - x0)) * 4;
        if (erase) {
          // destination-out: reduce existing alpha proportionally
          d[i + 3] = Math.round(d[i + 3] * (1 - srcA));
        } else {
          // source-over onto white layer: only alpha needs updating (RGB stays 255)
          const dstA = d[i + 3] / 255;
          d[i]     = 255;
          d[i + 1] = 255;
          d[i + 2] = 255;
          d[i + 3] = Math.round((srcA + dstA * (1 - srcA)) * 255);
        }
      }
    }

    ctx.putImageData(imgData, x0, y0);
    this._renderComposite();
  }

  _onPointerDown(e) {
    this._beginAction();
    this._noiseSeed = Math.random() * 0xFFFFFFFF | 0;
    const pt = this._getPos(e);
    this._isDrawing  = true;
    this._lastPt     = pt;
    this._axisOrigin = pt;
    this._axisLock   = null;
    this._applyStroke(pt, pt);  // dot at press point
    document.addEventListener('mousemove', this._onPointerMove);
    document.addEventListener('mouseup',   this._onPointerUp);
  }

  _onPointerMove(e) {
    if (!this._isDrawing) return;
    let pt = this._getPos(e);

    if (this._shiftDown) {
      // Determine axis lock on the first movement after mousedown
      if (this._axisLock === null) {
        const dx = Math.abs(pt.x - this._axisOrigin.x);
        const dy = Math.abs(pt.y - this._axisOrigin.y);
        this._axisLock = dx > dy ? 'x' : 'y';  // tie → Y
      }
      pt = this._axisLock === 'x'
        ? { x: pt.x,              y: this._axisOrigin.y }
        : { x: this._axisOrigin.x, y: pt.y };
    }

    this._applyStroke(this._lastPt, pt);
    this._lastPt = pt;
  }

  _onPointerUp() {
    if (this._isDrawing) {
      this._commitAction();
      this._emitLayersChange();  // refresh thumbnail once per stroke
    } else {
      this._cancelAction();
    }
    this._isDrawing = false;
    this._lastPt    = null;
    this._axisLock  = null;
    document.removeEventListener('mousemove', this._onPointerMove);
    document.removeEventListener('mouseup',   this._onPointerUp);
  }

  _onTouchStart(e) { e.preventDefault(); this._onPointerDown(e); }
  _onTouchMove(e)  { e.preventDefault(); this._onPointerMove(e); }

  // ── Selection / Transform tool ───────────────────────────────────────────

  _xformLocalToWorld(lx, ly) {
    const { cx, cy, θ } = this._sel;
    const c = Math.cos(θ), s = Math.sin(θ);
    return { x: cx + lx * c - ly * s, y: cy + lx * s + ly * c };
  }

  _xformWorldToLocal(wx, wy) {
    const { cx, cy, θ } = this._sel;
    const dx = wx - cx, dy = wy - cy;
    const c = Math.cos(θ), s = Math.sin(θ);
    return { lx: dx * c + dy * s, ly: -dx * s + dy * c };
  }

  _selHitTest(pt) {
    const sel = this._sel;
    if (sel.phase !== 'selected' || !sel.floatCanvas) return null;

    if (sel.warpCorners) {
      const rh = this._selWarpRotHandle();
      if (Math.hypot(pt.x - rh.x, pt.y - rh.y) <= 8) return 'rotate';
      for (const [k, c] of Object.entries(sel.warpCorners)) {
        if (Math.hypot(pt.x - c.x, pt.y - c.y) <= 8) return k;
      }
      const { tl, tr, bl, br } = sel.warpCorners;
      if (_pointInTriangle(pt.x,pt.y, tl.x,tl.y, tr.x,tr.y, br.x,br.y) ||
          _pointInTriangle(pt.x,pt.y, tl.x,tl.y, br.x,br.y, bl.x,bl.y)) return 'body';
      return null;
    }

    const { hw, hh } = sel;
    const rot = this._xformLocalToWorld(0, -(hh + 24));
    if (Math.hypot(pt.x - rot.x, pt.y - rot.y) <= 8) return 'rotate';
    for (const [k, lx, ly] of [['tl',-hw,-hh],['tr',hw,-hh],['bl',-hw,hh],['br',hw,hh]]) {
      const w = this._xformLocalToWorld(lx, ly);
      if (Math.hypot(pt.x - w.x, pt.y - w.y) <= 8) return k;
    }
    const { lx, ly } = this._xformWorldToLocal(pt.x, pt.y);
    if (Math.abs(lx) <= hw && Math.abs(ly) <= hh) return 'body';
    return null;
  }

  _selCutToFloat() {
    const sel = this._sel;
    const mr = sel.marqueeRect;
    const layer = this._selectedLayer();
    if (!layer || !mr) return false;
    const x = Math.max(0, Math.round(mr.x));
    const y = Math.max(0, Math.round(mr.y));
    const w = Math.round(Math.min(this.cw - x, mr.w));
    const h = Math.round(Math.min(this.ch - y, mr.h));
    if (w < 1 || h < 1) return false;
    const fc = document.createElement('canvas');
    fc.width = w; fc.height = h;
    const fctx = fc.getContext('2d');
    fctx.putImageData(layer.ctx.getImageData(x, y, w, h), 0, 0);
    layer.ctx.clearRect(x, y, w, h);
    sel.floatCanvas = fc;
    sel.floatCtx    = fctx;
    sel.cx = x + w / 2; sel.cy = y + h / 2;
    sel.hw = w / 2;     sel.hh = h / 2;
    sel.origHw = w / 2; sel.origHh = h / 2;
    sel.θ = 0; sel.flipX = false; sel.flipY = false;
    return true;
  }

  _selCommit() {
    const sel = this._sel;
    if (sel.floatCanvas) {
      const layer = this._selectedLayer();
      if (layer) {
        if (sel.warpCorners) {
          this._drawWarpedFloat(layer.ctx);
        } else {
          layer.ctx.save();
          layer.ctx.translate(sel.cx, sel.cy);
          layer.ctx.rotate(sel.θ);
          if (sel.flipX || sel.flipY) layer.ctx.scale(sel.flipX ? -1 : 1, sel.flipY ? -1 : 1);
          layer.ctx.drawImage(sel.floatCanvas, -sel.hw, -sel.hh, sel.hw * 2, sel.hh * 2);
          layer.ctx.restore();
        }
      }
    }
    this._selClearState();
    this._renderComposite();
    this._emitLayersChange();
  }

  _selClearState() {
    this._selStopMarch();
    this._sel = {
      phase: 'idle', handle: null,
      marqueeStart: null, marqueeRect: null, altMarqueeRect: null,
      floatCanvas: null, floatCtx: null,
      cx: 0, cy: 0, hw: 0, hh: 0, origHw: 0, origHh: 0, θ: 0, flipX: false, flipY: false,
      dragAnchorWorld: null, dragStart: null, hasMoved: false,
      warpCorners: null,
    };
    if (this._overlayCtx) {
      this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
    }
  }

  _selAltDeselect(altRect) {
    const sel = this._sel;
    if (!sel.floatCanvas) return;
    if (sel.warpCorners) return; // bilinear inverse-map not supported in warp mode
    const fw = sel.floatCanvas.width, fh = sel.floatCanvas.height;
    const fData = sel.floatCtx.getImageData(0, 0, fw, fh);
    const fd = fData.data;
    const layer = this._selectedLayer();
    const lData = layer ? layer.ctx.getImageData(0, 0, this.cw, this.ch) : null;
    const ld = lData ? lData.data : null;
    const c = Math.cos(sel.θ), s = Math.sin(sel.θ);
    const { cx, cy, hw, hh, origHw, origHh } = sel;

    for (let fv = 0; fv < fh; fv++) {
      for (let fu = 0; fu < fw; fu++) {
        const fi = (fv * fw + fu) * 4;
        if (fd[fi + 3] === 0) continue;
        // Map float pixel to world via current transform (including any flip)
        const lx = ((fu + 0.5) / fw - 0.5) * hw * 2 * (sel.flipX ? -1 : 1);
        const ly = ((fv + 0.5) / fh - 0.5) * hh * 2 * (sel.flipY ? -1 : 1);
        const wx = cx + lx * c - ly * s;
        const wy = cy + lx * s + ly * c;
        if (wx < altRect.x || wx > altRect.x + altRect.w ||
            wy < altRect.y || wy > altRect.y + altRect.h) continue;
        const srcA = fd[fi + 3];
        if (ld) {
          const ix = Math.round(wx), iy = Math.round(wy);
          if (ix >= 0 && ix < this.cw && iy >= 0 && iy < this.ch) {
            const li = (iy * this.cw + ix) * 4;
            const sa = srcA / 255, da = ld[li + 3] / 255;
            ld[li] = ld[li + 1] = ld[li + 2] = 255;
            ld[li + 3] = Math.round((sa + da * (1 - sa)) * 255);
          }
        }
        fd[fi + 3] = 0;
      }
    }
    sel.floatCtx.putImageData(fData, 0, 0);
    if (lData && layer) layer.ctx.putImageData(lData, 0, 0);
    this._renderComposite();

    // If float canvas is now empty, clear state
    const check = sel.floatCtx.getImageData(0, 0, fw, fh).data;
    for (let i = 3; i < check.length; i += 4) {
      if (check[i] > 0) return;
    }
    this._selClearState();
  }

  // Compute the rotation handle world position from the current warp corners.
  _selWarpRotHandle() {
    const { tl, tr, bl, br } = this._sel.warpCorners;
    const mid = { x: (tl.x + tr.x) / 2, y: (tl.y + tr.y) / 2 };
    const cx = (tl.x + tr.x + bl.x + br.x) / 4;
    const cy = (tl.y + tr.y + bl.y + br.y) / 4;
    const dx = mid.x - cx, dy = mid.y - cy;
    const len = Math.hypot(dx, dy) || 1;
    return { x: mid.x + dx / len * 24, y: mid.y + dy / len * 24, mid };
  }

  // Freeze current rigid transform into quad corners, baking any flip into the
  // float canvas so warped rendering can ignore flipX/flipY.
  _selEnterWarp() {
    const sel = this._sel;
    if (sel.flipX || sel.flipY) {
      const fc = document.createElement('canvas');
      fc.width = sel.floatCanvas.width; fc.height = sel.floatCanvas.height;
      const fctx = fc.getContext('2d');
      fctx.save();
      fctx.translate(fc.width / 2, fc.height / 2);
      fctx.scale(sel.flipX ? -1 : 1, sel.flipY ? -1 : 1);
      fctx.drawImage(sel.floatCanvas, -fc.width / 2, -fc.height / 2);
      fctx.restore();
      sel.floatCanvas = fc;
      sel.floatCtx    = fctx;
      sel.flipX = false; sel.flipY = false;
    }
    sel.warpCorners = {
      tl: this._xformLocalToWorld(-sel.hw, -sel.hh),
      tr: this._xformLocalToWorld( sel.hw, -sel.hh),
      bl: this._xformLocalToWorld(-sel.hw,  sel.hh),
      br: this._xformLocalToWorld( sel.hw,  sel.hh),
    };
  }

  // Collapse quad back to an axis-aligned bounding rect and resume rigid mode.
  _selExitWarp() {
    const sel = this._sel;
    if (!sel.warpCorners) return;
    const xs = Object.values(sel.warpCorners).map(c => c.x);
    const ys = Object.values(sel.warpCorners).map(c => c.y);
    sel.cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    sel.cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    sel.hw = (Math.max(...xs) - Math.min(...xs)) / 2;
    sel.hh = (Math.max(...ys) - Math.min(...ys)) / 2;
    sel.θ = 0; sel.flipX = false; sel.flipY = false;
    sel.warpCorners = null;
  }

  // Render the floating canvas warped to fit sel.warpCorners using two
  // affine-mapped triangles (TL-TR-BL and TR-BR-BL).
  _drawWarpedFloat(ctx) {
    const sel = this._sel;
    if (!sel.floatCanvas || !sel.warpCorners) return;
    const { tl, tr, bl, br } = sel.warpCorners;
    const fw = sel.floatCanvas.width, fh = sel.floatCanvas.height;
    _drawWarpTriangle(ctx, sel.floatCanvas,
      0,  0, fw,  0, 0, fh,
      tl.x, tl.y, tr.x, tr.y, bl.x, bl.y);
    _drawWarpTriangle(ctx, sel.floatCanvas,
      fw, 0, fw, fh, 0, fh,
      tr.x, tr.y, br.x, br.y, bl.x, bl.y);
  }

  _selOnPointerDown(e) {
    document.addEventListener('mousemove', this._selMoveHandler);
    document.addEventListener('mouseup',   this._selUpHandler);
    const pt  = this._getPos(e);
    const sel = this._sel;

    if (sel.phase === 'idle') {
      this._beginAction();
      sel.phase        = 'marquee';
      sel.marqueeStart = pt;
      sel.marqueeRect  = { x: pt.x, y: pt.y, w: 0, h: 0 };
      sel.hasMoved     = false;
      this._selRender();
      return;
    }

    if (sel.phase === 'selected') {
      this._beginAction(); // capture state before any selected-phase interaction

      // Alt+Shift+corner = enter / continue warp mode, move just that corner
      if (e.altKey && e.shiftKey) {
        const hit = this._selHitTest(pt);
        if (['tl','tr','bl','br'].includes(hit)) {
          if (!sel.warpCorners) this._selEnterWarp();
          sel.warpCorners[hit] = { x: pt.x, y: pt.y };
          sel.handle    = hit;
          sel.hasMoved  = false;
          sel.dragStart = { mx: pt.x, my: pt.y };
        } else {
          this._cancelAction(); // no-op
        }
        return;
      }

      // Alt alone = alt-deselect marquee
      if (e.altKey) {
        sel.phase          = 'alt-marquee';
        sel.marqueeStart   = pt;
        sel.altMarqueeRect = { x: pt.x, y: pt.y, w: 0, h: 0 };
        sel.hasMoved       = false;
        this._selRender();
        return;
      }

      // Normal drag — build shared dragStart, add warp context if needed
      const hit = this._selHitTest(pt);
      sel.hasMoved  = false;
      sel.dragStart = { mx: pt.x, my: pt.y, cx0: sel.cx, cy0: sel.cy };
      if (sel.warpCorners) {
        const { tl, tr, bl, br } = sel.warpCorners;
        sel.dragStart.corners0 = {
          tl: { ...tl }, tr: { ...tr }, bl: { ...bl }, br: { ...br },
        };
      }

      if (hit === null) {
        // Commit existing selection (one undo step), then start a fresh marquee.
        // _selCommit() resets this._sel, so use this._sel below.
        this._commitAction();  // push state-with-selection as undoable
        this._selCommit();     // paint float onto layer, clear selection
        this._beginAction();   // snapshot post-commit state for the new marquee
        this._sel.phase        = 'marquee';
        this._sel.marqueeStart = pt;
        this._sel.marqueeRect  = { x: pt.x, y: pt.y, w: 0, h: 0 };
        this._sel.hasMoved     = false;
        this._selRender();
        return;
      }
      if (hit === 'body') { sel.handle = 'body';    return; }
      if (hit === 'rotate') {
        sel.handle = 'rotate';
        if (sel.warpCorners) {
          const { tl, tr, bl, br } = sel.warpCorners;
          const wcx = (tl.x+tr.x+bl.x+br.x) / 4;
          const wcy = (tl.y+tr.y+bl.y+br.y) / 4;
          sel.dragStart.warpCx  = wcx;
          sel.dragStart.warpCy  = wcy;
          sel.dragStart.warpθ0  = Math.atan2(pt.x - wcx, -(pt.y - wcy));
        }
        return;
      }

      // Corner hit: if in warp mode, exit warp first then resize rigidly
      if (sel.warpCorners) this._selExitWarp();

      const anchorMap = { tl: [sel.hw, sel.hh], tr: [-sel.hw, sel.hh],
                          bl: [sel.hw,-sel.hh], br: [-sel.hw,-sel.hh] };
      const [alx, aly] = anchorMap[hit];
      sel.handle          = hit;
      sel.dragAnchorWorld = this._xformLocalToWorld(alx, aly);
      sel.dragStart       = { mx: pt.x, my: pt.y, hw0: sel.hw, hh0: sel.hh };
    }
  }

  _selOnPointerMove(e) {
    const pt  = this._getPos(e);
    const sel = this._sel;

    if (sel.phase === 'marquee') {
      sel.marqueeRect = {
        x: Math.min(pt.x, sel.marqueeStart.x), y: Math.min(pt.y, sel.marqueeStart.y),
        w: Math.abs(pt.x - sel.marqueeStart.x), h: Math.abs(pt.y - sel.marqueeStart.y),
      };
      sel.hasMoved = true;
      this._selRender();
      return;
    }

    if (sel.phase === 'alt-marquee') {
      sel.altMarqueeRect = {
        x: Math.min(pt.x, sel.marqueeStart.x), y: Math.min(pt.y, sel.marqueeStart.y),
        w: Math.abs(pt.x - sel.marqueeStart.x), h: Math.abs(pt.y - sel.marqueeStart.y),
      };
      sel.hasMoved = true;
      this._selRender();
      return;
    }

    if (sel.phase === 'selected') {
      if (sel.handle) {
        sel.hasMoved = true;

        // Warp corner drag: move that corner to the mouse
        if (sel.warpCorners && ['tl','tr','bl','br'].includes(sel.handle)) {
          sel.warpCorners[sel.handle] = { x: pt.x, y: pt.y };
          this._renderComposite();
          this._selRender();
          return;
        }

        if (sel.handle === 'body') {
          const { mx, my, cx0, cy0, corners0 } = sel.dragStart;
          if (sel.warpCorners && corners0) {
            const ddx = pt.x - mx, ddy = pt.y - my;
            for (const k of ['tl','tr','bl','br']) {
              sel.warpCorners[k] = { x: corners0[k].x + ddx, y: corners0[k].y + ddy };
            }
          } else {
            sel.cx = cx0 + (pt.x - mx);
            sel.cy = cy0 + (pt.y - my);
          }
          this._renderComposite();
          this._selRender();
          return;
        }

        if (sel.handle === 'rotate') {
          if (sel.warpCorners) {
            const { warpCx, warpCy, warpθ0, corners0 } = sel.dragStart;
            const newθ = Math.atan2(pt.x - warpCx, -(pt.y - warpCy));
            let dθ = newθ - warpθ0;
            if (this._shiftDown) dθ = Math.round(dθ / (Math.PI / 12)) * (Math.PI / 12);
            const cosD = Math.cos(dθ), sinD = Math.sin(dθ);
            for (const k of ['tl','tr','bl','br']) {
              const dx = corners0[k].x - warpCx, dy = corners0[k].y - warpCy;
              sel.warpCorners[k] = {
                x: warpCx + dx * cosD - dy * sinD,
                y: warpCy + dx * sinD + dy * cosD,
              };
            }
          } else {
            sel.θ = Math.atan2(pt.x - sel.cx, -(pt.y - sel.cy));
            if (this._shiftDown) sel.θ = Math.round(sel.θ / (Math.PI / 12)) * (Math.PI / 12);
          }
          this._renderComposite();
          this._selRender();
          return;
        }

        // Corner resize — anchor stays fixed.
        // Compute the local displacement from anchor to mouse, then optionally
        // project it onto the proportional-scale diagonal (Shift).  The center
        // is derived from the constrained extent so the anchor never drifts.
        const dx = pt.x - sel.dragAnchorWorld.x;
        const dy = pt.y - sel.dragAnchorWorld.y;
        const c  = Math.cos(sel.θ), s = Math.sin(sel.θ);
        const lx =  dx * c + dy * s;  // local x displacement
        const ly = -dx * s + dy * c;  // local y displacement

        let clx = lx, cly = ly;
        if (this._shiftDown) {
          // Project (lx, ly) onto the proportional-scale diagonal.
          // Using per-axis sign to pick the right quadrant so the projection
          // is continuous across the full drag range with no crossover jump.
          const dirX = (Math.sign(lx) || 1) * sel.origHw;
          const dirY = (Math.sign(ly) || 1) * sel.origHh;
          const t = (lx * dirX + ly * dirY) / (dirX * dirX + dirY * dirY);
          clx = t * dirX;
          cly = t * dirY;
        }

        // Minimum 2px total extent; preserve sign for flip support.
        if (Math.abs(clx) < 2) clx = Math.sign(clx) * 2 || 2;
        if (Math.abs(cly) < 2) cly = Math.sign(cly) * 2 || 2;

        // Flip when the mouse crosses to the anchor's side.
        // Each handle has an expected sign for clx/cly in the non-flipped state:
        //   br: (+,+)  tl: (-,-)  tr: (+,-)  bl: (-,+)
        const isTop  = sel.handle === 'tl' || sel.handle === 'tr';
        const isLeft = sel.handle === 'tl' || sel.handle === 'bl';
        sel.flipX = isLeft ? clx > 0 : clx < 0;
        sel.flipY = isTop  ? cly > 0 : cly < 0;
        sel.hw = Math.abs(clx) / 2;
        sel.hh = Math.abs(cly) / 2;
        // Center = anchor + half the rotated local extent (anchor stays fixed).
        sel.cx = sel.dragAnchorWorld.x + (clx / 2) * c - (cly / 2) * s;
        sel.cy = sel.dragAnchorWorld.y + (clx / 2) * s + (cly / 2) * c;
        this._renderComposite();
        this._selRender();
        return;
      }

      // No active drag — update cursor to show what can be grabbed
      const hit = this._selHitTest(pt);
      const warp = !!sel.warpCorners;
      const cursors = { body: 'move', rotate: 'grab',
                        tl: warp ? 'grab' : 'nw-resize', tr: warp ? 'grab' : 'ne-resize',
                        bl: warp ? 'grab' : 'sw-resize', br: warp ? 'grab' : 'se-resize' };
      if (this._overlayCanvas) {
        this._overlayCanvas.style.cursor = cursors[hit] ?? 'crosshair';
      }
    }
  }

  _selOnPointerUp(e) {
    document.removeEventListener('mousemove', this._selMoveHandler);
    document.removeEventListener('mouseup',   this._selUpHandler);
    const sel = this._sel;

    if (sel.phase === 'marquee') {
      const mr = sel.marqueeRect;
      if (!sel.hasMoved || !mr || mr.w < 2 || mr.h < 2) {
        this._selClearState(); this._cancelAction(); return;
      }
      if (!this._selCutToFloat()) {
        this._selClearState(); this._cancelAction(); return;
      }
      sel.marqueeRect = null;
      sel.phase       = 'selected';
      sel.handle      = null;
      this._commitAction(); // push pre-marquee state; new selection is now live
      this._renderComposite();
      this._emitLayersChange();
      this._selStartMarch();
      this._selRender();
      return;
    }

    if (sel.phase === 'alt-marquee') {
      const amr = sel.altMarqueeRect;
      if (sel.hasMoved && amr && amr.w >= 2 && amr.h >= 2) {
        this._commitAction(); // push pre-alt-deselect state
        this._selAltDeselect(amr);
      } else {
        this._cancelAction();
      }
      sel.altMarqueeRect = null;
      sel.handle         = null;
      if (sel.floatCanvas) { sel.phase = 'selected'; this._selRender(); }
      else this._selClearState();
      return;
    }

    if (sel.phase === 'selected') {
      if (sel.hasMoved) {
        this._commitAction(); // push pre-drag state
      } else {
        this._cancelAction();
      }
      sel.handle = null;
      this._emitLayersChange();
      this._selRender();
    }
  }

  _selStartMarch() {
    if (this._marchRaf) return;
    let last = 0;
    const tick = (t) => {
      if (this._sel.phase === 'idle') { this._marchRaf = null; return; }
      if (t - last > 80) {
        this._marchOffset = (this._marchOffset + 1) % 20;
        this._selRender();
        last = t;
      }
      this._marchRaf = requestAnimationFrame(tick);
    };
    this._marchRaf = requestAnimationFrame(tick);
  }

  _selStopMarch() {
    if (this._marchRaf) { cancelAnimationFrame(this._marchRaf); this._marchRaf = null; }
  }

  _selRender() {
    const oc = this._overlayCtx;
    if (!oc) return;
    oc.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
    const sel = this._sel;

    if (sel.phase === 'marquee' && sel.marqueeRect) {
      const r = sel.marqueeRect;
      oc.save();
      oc.strokeStyle    = 'rgba(200,220,255,0.8)';
      oc.lineWidth      = 1;
      oc.setLineDash([4, 4]);
      oc.lineDashOffset = -this._marchOffset;
      oc.strokeRect(r.x + 0.5, r.y + 0.5, r.w, r.h);
      oc.setLineDash([]);
      oc.restore();
      return;
    }

    if (sel.phase === 'selected' || sel.phase === 'alt-marquee') {
      this._selDrawHandles(oc);
    }

    if (sel.phase === 'alt-marquee' && sel.altMarqueeRect) {
      const r = sel.altMarqueeRect;
      oc.save();
      oc.strokeStyle    = 'rgba(255,120,80,0.8)';
      oc.lineWidth      = 1;
      oc.setLineDash([4, 4]);
      oc.lineDashOffset = -this._marchOffset;
      oc.strokeRect(r.x + 0.5, r.y + 0.5, r.w, r.h);
      oc.setLineDash([]);
      oc.restore();
    }
  }

  _selDrawHandles(oc) {
    const sel = this._sel;

    if (sel.warpCorners) {
      const { tl, tr, bl, br } = sel.warpCorners;
      const rh = this._selWarpRotHandle();

      // Quad border
      oc.save();
      oc.strokeStyle    = 'rgba(255,255,255,0.85)';
      oc.lineWidth      = 1;
      oc.setLineDash([5, 5]);
      oc.lineDashOffset = -this._marchOffset;
      oc.beginPath();
      oc.moveTo(tl.x, tl.y); oc.lineTo(tr.x, tr.y);
      oc.lineTo(br.x, br.y); oc.lineTo(bl.x, bl.y);
      oc.closePath();
      oc.stroke();
      oc.setLineDash([]);
      oc.restore();

      // Stem to rotation handle
      oc.save();
      oc.strokeStyle = 'rgba(255,255,255,0.5)';
      oc.lineWidth   = 1;
      oc.beginPath();
      oc.moveTo(rh.mid.x, rh.mid.y);
      oc.lineTo(rh.x, rh.y);
      oc.stroke();
      oc.restore();

      // Corner handles
      const HS = 5;
      oc.lineWidth   = 1;
      for (const c of Object.values(sel.warpCorners)) {
        oc.fillStyle   = '#fff';
        oc.strokeStyle = 'rgba(0,0,0,0.8)';
        oc.fillRect(c.x - HS, c.y - HS, HS * 2, HS * 2);
        oc.strokeRect(c.x - HS, c.y - HS, HS * 2, HS * 2);
      }

      // Rotation handle
      oc.beginPath();
      oc.arc(rh.x, rh.y, 6, 0, Math.PI * 2);
      oc.fillStyle   = '#fff';
      oc.fill();
      oc.strokeStyle = 'rgba(0,0,0,0.8)';
      oc.stroke();
      return;
    }

    const { hw, hh } = sel;
    if (!hw || !hh) return;

    const corners = [[-hw,-hh],[hw,-hh],[-hw,hh],[hw,hh]]
      .map(([lx, ly]) => this._xformLocalToWorld(lx, ly));
    const tc  = this._xformLocalToWorld(0, -hh);
    const rot = this._xformLocalToWorld(0, -(hh + 24));

    // Dashed border
    oc.save();
    oc.translate(sel.cx, sel.cy);
    oc.rotate(sel.θ);
    oc.strokeStyle    = 'rgba(255,255,255,0.85)';
    oc.lineWidth      = 1;
    oc.setLineDash([5, 5]);
    oc.lineDashOffset = -this._marchOffset;
    oc.strokeRect(-hw, -hh, hw * 2, hh * 2);
    oc.setLineDash([]);
    oc.restore();

    // Stem to rotation handle
    oc.save();
    oc.strokeStyle = 'rgba(255,255,255,0.5)';
    oc.lineWidth   = 1;
    oc.beginPath();
    oc.moveTo(tc.x, tc.y);
    oc.lineTo(rot.x, rot.y);
    oc.stroke();
    oc.restore();

    // Corner handles
    const HS = 5;
    oc.strokeStyle = 'rgba(0,0,0,0.8)';
    oc.lineWidth   = 1;
    for (const c of corners) {
      oc.fillStyle = '#fff';
      oc.fillRect(c.x - HS, c.y - HS, HS * 2, HS * 2);
      oc.strokeRect(c.x - HS, c.y - HS, HS * 2, HS * 2);
    }

    // Rotation handle
    oc.beginPath();
    oc.arc(rot.x, rot.y, 6, 0, Math.PI * 2);
    oc.fillStyle   = '#fff';
    oc.fill();
    oc.strokeStyle = 'rgba(0,0,0,0.8)';
    oc.stroke();
  }

  // ── Synthesis: Image → Audio ────────────────────────────────────────────

  pixelToAmp(v) {
    if (v < 1e-4) return 0;
    // Invert the log+gamma display compression to recover linear STFT magnitude.
    // No extra nonlinearity needed: Griffin-Lim handles leakage naturally.
    return Math.expm1(Math.pow(v, DISP_GAMMA) * DISP_LOG100) / 100;
  }

  _fft(re, im) {
    const n  = re.length;
    const br = n === this.fftSize ? this.brFft : bitReverse(n);
    for (let i = 0; i < n; i++) {
      const j = br[i];
      if (i < j) {
        let t = re[i]; re[i] = re[j]; re[j] = t;
        t = im[i]; im[i] = im[j]; im[j] = t;
      }
    }
    for (let len = 2; len <= n; len <<= 1) {
      const half = len >> 1;
      const ang  = -2 * Math.PI / len;
      const wRe  = Math.cos(ang), wIm = Math.sin(ang);
      for (let i = 0; i < n; i += len) {
        let curRe = 1, curIm = 0;
        for (let j = 0; j < half; j++) {
          const uRe = re[i+j], uIm = im[i+j];
          const vRe = re[i+j+half]*curRe - im[i+j+half]*curIm;
          const vIm = re[i+j+half]*curIm + im[i+j+half]*curRe;
          re[i+j]      = uRe + vRe; im[i+j]      = uIm + vIm;
          re[i+j+half] = uRe - vRe; im[i+j+half] = uIm - vIm;
          const nRe = curRe*wRe - curIm*wIm;
          curIm = curRe*wIm + curIm*wRe; curRe = nRe;
        }
      }
    }
  }

  _ifft(re, im) {
    const n = re.length;
    for (let i = 0; i < n; i++) im[i] = -im[i];
    this._fft(re, im);
    const inv = 1 / n;
    for (let i = 0; i < n; i++) { re[i] *= inv; im[i] = -im[i] * inv; }
  }

  async synthesize(glIters) {
    this._setStatus('Synthesizing…');
    const GL_ITERS = glIters;
    const hopSize  = this.totalSamples / this.cw;
    const FFT_SIZE = this.fftSize;
    const CW = this.cw, CH = this.ch;

    // Build target magnitude array from canvas pixels. Each STFT bin k maps to a
    // fractional canvas row via the log-frequency axis; linearly interpolate
    // between the two nearest rows so the magnitude spectrum transitions
    // smoothly across bin boundaries rather than stepping.
    const imgData = this.ctx.getImageData(0, 0, CW, CH);
    const d       = imgData.data;
    const targetMag = new Array(CW);
    for (let x = 0; x < CW; x++) {
      const mag = new Float32Array(this.glBins);
      for (let k = 1; k <= this.freqMaxBin; k++) {
        const ylo = this.binRowLo[k], yhi = this.binRowHi[k];
        const frac = this.binRowFrac[k];
        const blo  = (ylo * CW + x) * 4;
        const bhi  = (yhi * CW + x) * 4;
        const vlo  = (0.299*d[blo] + 0.587*d[blo+1] + 0.114*d[blo+2]) / 255;
        const vhi  = (0.299*d[bhi] + 0.587*d[bhi+1] + 0.114*d[bhi+2]) / 255;
        mag[k] = this.pixelToAmp(vlo + (vhi - vlo) * frac);
      }
      targetMag[x] = mag;
    }

    // Complex STFT arrays — initialised with target magnitudes + random phases.
    // Full FFT_SIZE arrays (not just the positive half) so _ifft works directly.
    const specRe = new Array(CW);
    const specIm = new Array(CW);
    for (let x = 0; x < CW; x++) {
      const re = new Float32Array(FFT_SIZE);
      const im = new Float32Array(FFT_SIZE);
      const mg = targetMag[x];
      for (let k = 1; k <= this.freqMaxBin; k++) {
        const phi = Math.random() * 2 * Math.PI;
        re[k]            =  mg[k] * Math.cos(phi);
        im[k]            =  mg[k] * Math.sin(phi);
        re[FFT_SIZE - k] =  re[k];   // Hermitian symmetry for real output
        im[FFT_SIZE - k] = -im[k];
      }
      specRe[x] = re;
      specIm[x] = im;
    }

    const tmpRe  = new Float32Array(FFT_SIZE);
    const tmpIm  = new Float32Array(FFT_SIZE);
    const signal = new Float32Array(this.totalSamples);

    // ISTFT via WOLA: IFFT each frame, multiply by Hann synthesis window,
    // overlap-add, then divide by the precomputed sum-of-squared-windows.
    const istft = () => {
      signal.fill(0);
      for (let x = 0; x < CW; x++) {
        tmpRe.set(specRe[x]); tmpIm.set(specIm[x]);
        this._ifft(tmpRe, tmpIm);
        const s = Math.round(x * hopSize);
        for (let n = 0; n < FFT_SIZE; n++) {
          const p = s + n;
          if (p < this.totalSamples) signal[p] += tmpRe[n] * this.hann[n];
        }
      }
      // Clamp divisor to wolaPeak: samples at the signal edges where only a
      // handful of frames overlap would otherwise be amplified by 1000×+,
      // overwhelming the peak-normalisation and making everything else inaudible.
      for (let p = 0; p < this.totalSamples; p++) {
        signal[p] /= Math.max(this.wolaNorm[p], this.wolaPeak);
      }
    };

    // Griffin-Lim iterations: ISTFT → STFT → replace magnitudes → repeat. Each
    // cycle brings the phases closer to being consistent with the target
    // magnitude spectrogram, eliminating inter-bin beating from random phases.
    for (let iter = 0; iter < GL_ITERS; iter++) {
      istft();

      for (let x = 0; x < CW; x++) {
        const s = Math.round(x * hopSize);
        for (let n = 0; n < FFT_SIZE; n++) {
          const p  = s + n;
          tmpRe[n] = (p < this.totalSamples ? signal[p] : 0) * this.hann[n];
          tmpIm[n] = 0;
        }
        this._fft(tmpRe, tmpIm);

        const mg = targetMag[x];
        const re = specRe[x];
        const im = specIm[x];
        for (let k = 1; k <= this.freqMaxBin; k++) {
          const r = Math.sqrt(tmpRe[k]*tmpRe[k] + tmpIm[k]*tmpIm[k]);
          if (r > 1e-10) {
            const sc = mg[k] / r;
            re[k] = tmpRe[k] * sc;
            im[k] = tmpIm[k] * sc;
          } else {
            const phi = Math.random() * 2 * Math.PI;
            re[k] = mg[k] * Math.cos(phi);
            im[k] = mg[k] * Math.sin(phi);
          }
          re[FFT_SIZE - k] =  re[k];
          im[FFT_SIZE - k] = -im[k];
        }
        re[0] = im[0] = re[FFT_SIZE >> 1] = im[FFT_SIZE >> 1] = 0;
      }

      this._setProgress(Math.round((iter + 1) / GL_ITERS * 100));
      await new Promise(r => setTimeout(r, 0));  // yield so the progress bar repaints
    }

    istft();  // final reconstruction from converged phases

    let maxAbs = 1e-10;
    for (let p = 0; p < this.totalSamples; p++) maxAbs = Math.max(maxAbs, Math.abs(signal[p]));
    const output = new Float32Array(this.totalSamples);
    for (let p = 0; p < this.totalSamples; p++) output[p] = signal[p] / maxAbs;

    this._setProgress(0);
    this._setStatus('Ready');
    return output;
  }

  // ── WAV Encoder ─────────────────────────────────────────────────────────

  encodeWAV(samples) {
    const dataBytes = samples.length * 2;
    const buf  = new ArrayBuffer(44 + dataBytes);
    const view = new DataView(buf);
    const str  = (off, s) => { for (let i=0;i<s.length;i++) view.setUint8(off+i, s.charCodeAt(i)); };
    str(0, 'RIFF');
    view.setUint32(4, 36 + dataBytes, true);
    str(8, 'WAVE');
    str(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);           // PCM
    view.setUint16(22, 1, true);           // mono
    view.setUint32(24, this.sampleRate, true);
    view.setUint32(28, this.sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    str(36, 'data');
    view.setUint32(40, dataBytes, true);
    let off = 44;
    for (const s of samples) {
      const v = Math.max(-1, Math.min(1, s));
      view.setInt16(off, v < 0 ? v * 32768 : v * 32767, true);
      off += 2;
    }
    return buf;
  }

  // ── Playback ────────────────────────────────────────────────────────────

  _getAudioCtx() {
    if (!this.audioCtx) this.audioCtx = new AudioContext();
    return this.audioCtx;
  }

  async play() {
    this.stop();
    if (!this._playCache) {
      this._emit('busychange', { busy: true });
      this._playCache = await this.synthesize(this.glItersPlay);
      this._emit('busychange', { busy: false });
    }
    const samples = this._playCache;

    const ac  = this._getAudioCtx();
    const buf = ac.createBuffer(1, samples.length, this.sampleRate);
    buf.getChannelData(0).set(samples);
    this.playSource = ac.createBufferSource();
    this.playSource.buffer = buf;
    this.playSource.connect(ac.destination);
    this.playSource.start();
    this.playStart = ac.currentTime;

    this._emit('playstatechange', { playing: true });
    this._setStatus('Playing…');
    this._animatePlayhead();
    this.playSource.onended = () => { this.stop(); this._setStatus('Ready'); };
  }

  stop() {
    if (this.playSource) { try { this.playSource.stop(); } catch (e) {} this.playSource = null; }
    if (this._animFrame) { cancelAnimationFrame(this._animFrame); this._animFrame = null; }
    this._emit('playstatechange', { playing: false });
  }

  _animatePlayhead() {
    if (!this.playSource || !this.audioCtx) return;
    const elapsed  = this.audioCtx.currentTime - this.playStart;
    const progress = Math.min(elapsed / this.duration, 1);
    this._emit('playheadmove', { progress });
    if (elapsed < this.duration + 0.1) {
      this._animFrame = requestAnimationFrame(() => this._animatePlayhead());
    }
  }

  // ── Export ──────────────────────────────────────────────────────────────

  async exportWav() {
    this._emit('busychange', { busy: true });
    const samples = await this.synthesize(this.glItersExport);
    this._emit('busychange', { busy: false });

    const wav  = this.encodeWAV(samples);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${this._safeFilename()}.wav`; a.click();
    URL.revokeObjectURL(url);
  }

  async exportOgg() {
    this._emit('busychange', { busy: true });
    const samples = await this.synthesize(this.glItersExport);
    this._setStatus('Encoding Ogg Vorbis…');
    const blob = await encodeOggVorbis(samples, this.sampleRate);
    this._emit('busychange', { busy: false });
    this._setStatus('Ready');

    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href = url; a.download = `${this._safeFilename()}.ogg`; a.click();
    URL.revokeObjectURL(url);
  }

  // ── Import: PNG → Canvas ───────────────────────────────────────────────

  importPngFile(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        // Draw to a temp canvas to sample pixel data
        const tc = document.createElement('canvas');
        tc.width = this.cw; tc.height = this.ch;
        const tctx = tc.getContext('2d');
        tctx.drawImage(img, 0, 0, this.cw, this.ch);
        const src = tctx.getImageData(0, 0, this.cw, this.ch).data;
        URL.revokeObjectURL(url);

        const layerName = file.name.replace(/\.[^.]+$/, '') || 'Imported Image';
        const layer = this._makeLayer(layerName);

        // Convert to white-on-transparent: grayscale luma → alpha channel.
        const imgData = new ImageData(this.cw, this.ch);
        const dst = imgData.data;
        for (let i = 0; i < this.cw * this.ch; i++) {
          const luma = Math.round(0.299*src[i*4] + 0.587*src[i*4+1] + 0.114*src[i*4+2]);
          dst[i*4]   = 255;   // R
          dst[i*4+1] = 255;   // G
          dst[i*4+2] = 255;   // B
          dst[i*4+3] = luma;  // A = brightness
        }
        layer.ctx.putImageData(imgData, 0, 0);

        this._commitSelectionToOwnLayer();
        this.layers.push(layer);
        this.selectedLayerId = layer.id;
        this._renderComposite();
        this._emitLayersChange();
        this._setStatus('Ready');
        resolve();
      };
      img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
      img.src = url;
    });
  }

  // ── Import: Audio → Spectrogram ────────────────────────────────────────

  async importAudioFile(file) {
    const name = file.name.replace(/\.[^.]+$/, '') || 'Imported Audio';
    await this._importAudioBuffer(await file.arrayBuffer(), name);
  }

  async importAudioUrl(url, name) {
    this._setStatus('Fetching audio…');
    let arrayBuf;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      arrayBuf = await res.arrayBuffer();
    } catch (err) {
      this._setStatus('Fetch failed: ' + err.message);
      return;
    }
    if (!name){
      name = url.split('/').pop().replace(/[?#].*$/, '').replace(/\.[^.]+$/, '') || 'Imported Audio';
    }
    await this._importAudioBuffer(arrayBuf, name);
  }

  async _importAudioBuffer(arrayBuf, name) {
    this._setStatus('Decoding audio…');
    this._setProgress(10);
    const ac = new AudioContext({ sampleRate: this.sampleRate });
    let audioBuf;
    try {
      audioBuf = await ac.decodeAudioData(arrayBuf);
    } catch (err) {
      this._setStatus('Error: ' + err.message);
      this._setProgress(0);
      return;
    }

    this._setProgress(30);
    this._setStatus('Computing spectrogram…');

    const CW = this.cw, CH = this.ch, FFT_SIZE = this.fftSize;

    // Mix to mono, limit to duration
    const maxSamp = Math.min(audioBuf.length, this.totalSamples);
    const samples = new Float32Array(this.totalSamples);
    const nCh = audioBuf.numberOfChannels;
    for (let ch = 0; ch < nCh; ch++) {
      const d = audioBuf.getChannelData(ch);
      for (let i = 0; i < maxSamp; i++) samples[i] += d[i] / nCh;
    }

    const hopSize = this.totalSamples / CW;

    // Two-pass: compute mags then normalise
    const allMags = new Array(CW);
    let globalMax = 1e-10;

    for (let x = 0; x < CW; x++) {
      const re = new Float32Array(FFT_SIZE);
      const im = new Float32Array(FFT_SIZE);
      const s0 = Math.round(x * hopSize);
      for (let n = 0; n < FFT_SIZE; n++) {
        const idx = s0 + n;
        re[n] = (idx < this.totalSamples ? samples[idx] : 0) * this.hann[n];
      }
      this._fft(re, im);

      const mags = new Float32Array(FFT_SIZE / 2);
      for (let k = 0; k < FFT_SIZE / 2; k++) {
        mags[k] = Math.sqrt(re[k]*re[k] + im[k]*im[k]);
        if (mags[k] > globalMax) globalMax = mags[k];
      }
      allMags[x] = mags;
      if (x % 64 === 0) this._setProgress(30 + (x / CW) * 50);
    }

    this._setProgress(85);

    // Write spectrogram as white-on-transparent to a new layer.
    const layer  = this._makeLayer(name);
    const imgData = new ImageData(CW, CH);
    for (let x = 0; x < CW; x++) {
      const mags = allMags[x];
      for (let y = 0; y < CH; y++) {
        const k      = this._yToBin(y);
        const linVal = mags[k] / globalMax;
        const logVal = Math.log1p(linVal * 100) / Math.log1p(100);
        const bright = Math.round(Math.pow(Math.max(0, logVal), 0.85) * 255);
        const idx    = (y * CW + x) * 4;
        imgData.data[idx]   = 255;    // R: always white
        imgData.data[idx+1] = 255;    // G: always white
        imgData.data[idx+2] = 255;    // B: always white
        imgData.data[idx+3] = bright; // A: brightness as alpha
      }
    }
    layer.ctx.putImageData(imgData, 0, 0);

    this._commitSelectionToOwnLayer();
    this.layers.push(layer);
    this.selectedLayerId = layer.id;
    this._renderComposite();
    this._emitLayersChange();
    this._setProgress(100);
    this._setStatus(`Loaded: ${audioBuf.duration.toFixed(2)}s @ ${audioBuf.sampleRate}Hz${audioBuf.duration > this.duration ? ' (truncated to 2s)' : ''}`);
    setTimeout(() => this._setProgress(0), 600);
  }

  // ── Save / Load ─────────────────────────────────────────────────────────────
  //
  // Binary format ".spnt" — version 2 (version 1 is still read; it had no layer
  // flags byte, and its layers all load visible):
  //   Bytes  0-3   Magic "SPNT"
  //   Byte   4     Version (2)
  //   Bytes  5-6   Canvas width  (uint16 LE)
  //   Bytes  7-8   Canvas height (uint16 LE)
  //   Byte   9     Layer count (uint8)
  //   Byte   10    Selected layer index, 0 = bottom (uint8)
  //   Byte   11    Session name byte length (uint8)
  //   Bytes  12..  Session name (UTF-8)
  //   For each layer (bottom → top):
  //     Byte   0     Name byte length (uint8)
  //     Bytes  1..N  Name (UTF-8)
  //     Byte   N+1   Flags (uint8): bit 0 = visible   [v2+ only]
  //     Bytes  N+2..N+5  Compressed data length (uint32 LE)
  //     Bytes  N+6..     zlib-deflate RGBA pixel data (cw × ch × 4 bytes uncompressed)
  //
  // localStorage key layout (prefix = this.storagePrefix, default "spnt:"):
  //   {prefix}__index  — JSON array of { id, name, savedAt } entries
  //   {prefix}data:{id} — base64-encoded binary save data

  // Commit any floating selection before saving (shared by save() and saveToStorage()).
  _commitForSave() {
    if (this._sel.phase === 'selected') {
      this._beginAction();
      this._selCommit();
      this._commitAction();
    } else if (this._sel.phase !== 'idle') {
      this._selClearState();
      this._selRender();
    }
  }

  // Build the binary save buffer from the current layer state.
  async _buildSaveBuffer() {
    const selIdx   = Math.max(0, this.layers.findIndex(l => l.id === this.selectedLayerId));
    const sessName = _utf8Trunc(this.sessionName, 255);

    const parts = await Promise.all(this.layers.map(async (layer) => ({
      name:  _utf8Trunc(layer.name, 255),
      flags: layer.visible ? 1 : 0,
      data:  await _deflate(layer.ctx.getImageData(0, 0, this.cw, this.ch).data.buffer),
    })));

    const HEADER = 12 + sessName.byteLength;
    let totalSize = HEADER;
    for (const { name, data } of parts) totalSize += 1 + name.byteLength + 1 + 4 + data.byteLength;

    const out  = new Uint8Array(totalSize);
    const view = new DataView(out.buffer);
    out[0] = 0x53; out[1] = 0x50; out[2] = 0x4E; out[3] = 0x54; // "SPNT"
    view.setUint8(4, 2);
    view.setUint16(5, this.cw, true);
    view.setUint16(7, this.ch, true);
    view.setUint8(9, this.layers.length);
    view.setUint8(10, selIdx);
    view.setUint8(11, sessName.byteLength);
    out.set(sessName, 12);

    let off = HEADER;
    for (const { name, flags, data } of parts) {
      view.setUint8(off, name.byteLength); off += 1;
      out.set(name, off);                  off += name.byteLength;
      view.setUint8(off, flags);           off += 1;
      view.setUint32(off, data.byteLength, true); off += 4;
      out.set(new Uint8Array(data), off);  off += data.byteLength;
    }
    return out;
  }

  // Parse and apply a binary save buffer. Throws on format errors.
  async _applyBuffer(u8) {
    const view = new DataView(u8.buffer);
    const dec  = new TextDecoder();

    if (u8[0] !== 0x53 || u8[1] !== 0x50 || u8[2] !== 0x4E || u8[3] !== 0x54) {
      throw new Error('Not a Spectral Painter file');
    }
    const version = view.getUint8(4);
    if (version !== 1 && version !== 2) throw new Error(`Unsupported file version: ${version}`);

    const fcw     = view.getUint16(5, true);
    const fch     = view.getUint16(7, true);
    const nlayers = view.getUint8(9);
    const selIdx  = view.getUint8(10);
    const snLen   = view.getUint8(11);
    const sessName = dec.decode(u8.subarray(12, 12 + snLen));

    let off = 12 + snLen;
    const layerDatas = [];
    for (let i = 0; i < nlayers; i++) {
      const nameLen = view.getUint8(off); off += 1;
      const name    = dec.decode(u8.subarray(off, off + nameLen)); off += nameLen;
      let visible = true;
      if (version >= 2) { visible = (view.getUint8(off) & 1) === 1; off += 1; }
      const dataLen = view.getUint32(off, true); off += 4;
      const imgBuf  = await _inflate(u8.slice(off, off + dataLen)); off += dataLen;
      layerDatas.push({ name, visible, imgBuf });
    }

    if (this._sel.phase !== 'idle') { this._selClearState(); this._selRender(); }
    this._undoStack = []; this._redoStack = [];
    this._emitHistoryChange();

    this.layers = [];
    for (const { name, visible, imgBuf } of layerDatas) {
      const layer = this._makeLayer(name);
      layer.visible = visible;
      layer.ctx.putImageData(new ImageData(new Uint8ClampedArray(imgBuf), fcw, fch), 0, 0);
      this.layers.push(layer);
    }
    this.selectedLayerId = this.layers[Math.min(selIdx, this.layers.length - 1)].id;
    this.setName(sessName);
    this._renderComposite();
    this._emitLayersChange();
  }

  // ── Disk save / load ────────────────────────────────────────────────────────

  async save() {
    this._commitForSave();
    this._setStatus('Saving…');
    const out  = await this._buildSaveBuffer();
    const blob = new Blob([out], { type: 'application/octet-stream' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${this._safeFilename()}.${this.fileExtension}`; a.click();
    URL.revokeObjectURL(url);
    this._setStatus('Ready');
  }

  async load(file) {
    this._setStatus('Loading…');
    try {
      await this._applyBuffer(new Uint8Array(await file.arrayBuffer()));
      this._setStatus('Ready');
    } catch (err) {
      this._setStatus(`Load failed: ${err.message}`);
    }
  }

  // ── Browser storage save / load ─────────────────────────────────────────────

  // Returns { success: true, id } or { success: false, error: string }.
  async saveToStorage() {
    this._commitForSave();
    this._setStatus('Saving to browser…');

    const out = await this._buildSaveBuffer();
    const b64 = _toBase64(out);
    const id  = Date.now().toString(36);
    const px  = this.storagePrefix;

    try {
      localStorage.setItem(`${px}data:${id}`, b64);
    } catch (e) {
      const msg = (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')
        ? 'Browser storage is full' : e.message;
      this._setStatus(`Save failed: ${msg}`);
      return { success: false, error: msg };
    }

    // Update index — if this also fails, roll back the data entry.
    let index = [];
    try { index = JSON.parse(localStorage.getItem(`${px}__index`) || '[]'); } catch {}
    index.push({ id, name: this.sessionName || 'untitled', savedAt: Date.now() });
    try {
      localStorage.setItem(`${px}__index`, JSON.stringify(index));
    } catch (e) {
      localStorage.removeItem(`${px}data:${id}`);
      this._setStatus('Save failed: Browser storage is full');
      return { success: false, error: 'Browser storage is full' };
    }

    this._setStatus('Saved to browser.');
    return { success: true, id };
  }

  async loadFromStorage(id) {
    this._setStatus('Loading…');
    try {
      const b64 = localStorage.getItem(`${this.storagePrefix}data:${id}`);
      if (!b64) throw new Error('Session not found in storage');
      await this._applyBuffer(_fromBase64(b64));
      this._setStatus('Ready');
    } catch (err) {
      this._setStatus(`Load failed: ${err.message}`);
    }
  }

  downloadFromStorage(id) {
    const px  = this.storagePrefix;
    const b64 = localStorage.getItem(`${px}data:${id}`);
    if (!b64) return;

    let name = 'untitled';
    try {
      const entry = JSON.parse(localStorage.getItem(`${px}__index`) || '[]').find(s => s.id === id);
      if (entry?.name) name = entry.name;
    } catch {}

    const safeName = name.trim().replace(/[/\\:*?"<>|]/g, '').trim().slice(0, 100) || 'untitled';
    const u8   = _fromBase64(b64);
    const blob = new Blob([u8], { type: 'application/octet-stream' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${safeName}.${this.fileExtension}`; a.click();
    URL.revokeObjectURL(url);
  }

  deleteFromStorage(id) {
    const px = this.storagePrefix;
    localStorage.removeItem(`${px}data:${id}`);
    let index = [];
    try { index = JSON.parse(localStorage.getItem(`${px}__index`) || '[]'); } catch {}
    const updated = index.filter(s => s.id !== id);
    try { localStorage.setItem(`${px}__index`, JSON.stringify(updated)); } catch {}
  }

  // Returns [{id, name, savedAt}] sorted newest-first.
  listStorageSessions() {
    try {
      const index = JSON.parse(localStorage.getItem(`${this.storagePrefix}__index`) || '[]');
      return [...index].sort((a, b) => b.savedAt - a.savedAt);
    } catch { return []; }
  }

  // ── Feedback events (for a view layer to bind to) ──────────────────────

  _emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { detail }));
  }

  _setStatus(message)  { this._emit('statuschange', { message }); }
  _setProgress(percent) { this._emit('progresschange', { percent }); }
}
